import { ChangeEvent } from 'react'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useAppDispatch, useAppSelector } from '@redux/hooks'
import { BasicInfoSection, SettingsSection, NotificationsSection } from './components'
import * as css from './styles'
import { BasicInfo, CustomEvent, Options } from './types'
import { createCustomEvent } from './utils'
import { setTemplateDetails as setTemplateDetailsAction } from '../template.actions'

export const Setup = () => {
  const dispatch = useAppDispatch()
  const [ENABLE_AUTO_SUBMIT = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_AUTO_SUBMIT]),
  )
  const { allow_approval } = useAppSelector(state => state.Auth.account)
  const { name, description, folder_id, settings } = useAppSelector(
    (state: any) => state.Template.template,
  )
  const basicInfo: BasicInfo = {
    name,
    description,
    folder_id,
  }

  return (
    <div className="h-full bg-white">
      <css.Section elevation={0}>
        <BasicInfoSection
          basicInfo={basicInfo}
          handleChange={handleChange}
          handleChangeCustom={handleChangeCustom}
        />
      </css.Section>
      <css.Section elevation={0}>
        <SettingsSection
          allow_approval={!!allow_approval}
          handleChangeCustom={handleChangeCustom}
          handleChangeApprovers={handleChangeApprovers}
        />
      </css.Section>
      {ENABLE_AUTO_SUBMIT && settings?.enable_auto_submit ? (
        <css.Section elevation={0}>
          <NotificationsSection />
        </css.Section>
      ) : null}
    </div>
  )

  /**
   * The onChange handler for text fields.
   * This also dispatches an action to update all template details.
   * @param {ChangeEvent<HTMLInputElement>} e - React ChangeEvent
   */
  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const updatedTemplateDetails = {
      [e.target.name]: e.target.value,
    }

    dispatch(setTemplateDetailsAction(updatedTemplateDetails))
  }

  /**
   * The onChange handler for checkbox and autocomplete inputs.
   * This also clears out approvers if needed and dispatches an action to update all template details.
   * @param {CustomEvent} customEvent - An object with a key and value
   */
  function handleChangeCustom({ key, value }: CustomEvent) {
    const event = createCustomEvent({ key, value })
    const resetApprovers = key === Options.REQUIRE_APPROVAL && value === false

    if (resetApprovers) {
      handleChangeApprovers([])
    }

    const updatedTemplateDetails = (() => {
      if (
        [Options.ENABLE_AUTO_SUBMIT, Options.REQUIRE_APPROVAL, Options.REQUIRE_IDENTIFIER].includes(
          key,
        )
      ) {
        return {
          settings: {
            ...settings,
            [event.target.name]: event.target.value,
          },
        }
      }

      return { [event.target.name]: event.target.value }
    })()

    dispatch(setTemplateDetailsAction(updatedTemplateDetails))
  }

  /**
   * The onChange handler for updating approvers.
   * This also clears out approvers if needed and dispatches an action to update all template details.
   * @param {object[]} newValues - An array of user objects
   */
  function handleChangeApprovers(newValues) {
    dispatch(
      setTemplateDetailsAction({
        approvers: newValues,
      }),
    )
  }
}
